import React, { useState, useEffect } from 'react'
import { Form, Input, InputNumber, Select, DatePicker, Row, Col, Divider, Alert, Space, Button } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import moment from 'moment'
import { BillingEnums } from '../../utils/enums'
import { getInvoiceDetails } from '../../backend-integration/api/creditNotes'
import { searchInvoices } from '../../../Invoices/utils'

const { Option } = Select
const { TextArea } = Input

const CreditNoteForm = ({ form, editingCreditNote, preSelectedPatient, onFinish }) => {
  const [invoiceDetails, setInvoiceDetails] = useState(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [maxAmount, setMaxAmount] = useState(0)

  useEffect(() => {
    if (editingCreditNote?.invoiceId) {
      fetchInvoiceDetails(editingCreditNote.invoiceId)
    }
  }, [editingCreditNote])

  // Pre-select patient if provided
  useEffect(() => {
    if (preSelectedPatient) {
      form.setFieldsValue({
        patientId: preSelectedPatient._id,
        patientName: preSelectedPatient.name,
        billingId: preSelectedPatient.billingId
      })
    }
  }, [preSelectedPatient, form])

  const fetchInvoiceDetails = async (invoiceId) => {
    setLoadingInvoice(true)
    try {
      const details = await getInvoiceDetails(invoiceId)
      setInvoiceDetails(details)
      setMaxAmount(details.outstandingAmount || details.totalAmount)
    } catch (error) {
      console.error('Error fetching invoice details:', error)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const handleInvoiceSearch = async () => {
    const invoiceNo = form.getFieldValue('invoiceNo')
    if (!invoiceNo) return

    setLoadingInvoice(true)
    try {
      const invoices = await searchInvoices({ invoiceNo })
      if (invoices.length > 0) {
        const invoice = invoices[0]
        form.setFieldsValue({
          invoiceId: invoice._id,
          patientId: invoice.patientId,
          patientName: invoice.patientName
        })
        setInvoiceDetails(invoice)
        setMaxAmount(invoice.outstandingAmount || invoice.totalAmount)
      }
    } catch (error) {
      console.error('Error searching invoice:', error)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const validateAmount = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Please enter an amount'))
    }
    if (value <= 0) {
      return Promise.reject(new Error('Amount must be greater than 0'))
    }
    if (maxAmount && value > maxAmount) {
      return Promise.reject(new Error(`Amount cannot exceed invoice amount of R${maxAmount.toFixed(2)}`))
    }
    return Promise.resolve()
  }

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={onFinish}
      initialValues={{
        date: moment(),
        status: BillingEnums.creditNoteStatus.DRAFT,
        applyTo: 'patient'
      }}
    >
      {preSelectedPatient && (
        <>
          <Alert
            message='Selected Patient'
            description={
              <div>
                <strong>{preSelectedPatient.name}</strong>
                <br />
                Billing ID: {preSelectedPatient.billingId}
                {preSelectedPatient.fileNo && (
                  <>
                    <br />
                    File Number: {preSelectedPatient.fileNo}
                  </>
                )}
              </div>
            }
            type='info'
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Divider>Credit Note for Patient</Divider>
        </>
      )}

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='date' label='Date' rules={[{ required: true, message: 'Please select date' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='status' label='Status' rules={[{ required: true }]}>
            <Select disabled={editingCreditNote}>
              {Object.values(BillingEnums.creditNoteStatus).map((status) => (
                <Option key={status} value={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider>Invoice Details</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='invoiceNo'
            label='Invoice Number'
            rules={[{ required: true, message: 'Please enter invoice number' }]}
          >
            <Input
              placeholder='Enter invoice number'
              suffix={
                <Button icon={<SearchOutlined />} loading={loadingInvoice} onClick={handleInvoiceSearch} type='text' />
              }
              onPressEnter={handleInvoiceSearch}
              disabled={editingCreditNote}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='patientName' label='Patient Name'>
            <Input disabled />
          </Form.Item>
        </Col>
      </Row>

      {invoiceDetails && (
        <Alert
          message='Invoice Details'
          description={
            <Space direction='vertical'>
              <div>Invoice Amount: R{invoiceDetails.totalAmount?.toFixed(2)}</div>
              <div>Outstanding: R{invoiceDetails.outstandingAmount?.toFixed(2)}</div>
              <div>Medical Aid: {invoiceDetails.medicalAid || 'Private'}</div>
            </Space>
          }
          type='info'
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item name='invoiceId' hidden>
        <Input />
      </Form.Item>
      <Form.Item name='patientId' hidden>
        <Input />
      </Form.Item>

      <Divider>Credit Note Details</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='amount' label='Amount' rules={[{ required: true }, { validator: validateAmount }]}>
            <InputNumber
              style={{ width: '100%' }}
              formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/R\s?|(,*)/g, '')}
              precision={2}
              min={0}
              max={maxAmount}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='applyTo' label='Apply To' rules={[{ required: true }]}>
            <Select>
              <Option value='patient'>Patient Portion</Option>
              <Option value='medical_aid'>Medical Aid Portion</Option>
              <Option value='both'>Both</Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='reasonCode'
            label='Reason Code'
            rules={[{ required: true, message: 'Please select reason' }]}
          >
            <Select>
              <Option value='BILLING_ERROR'>Billing Error</Option>
              <Option value='SERVICE_NOT_RENDERED'>Service Not Rendered</Option>
              <Option value='DISCOUNT'>Discount</Option>
              <Option value='DUPLICATE'>Duplicate Billing</Option>
              <Option value='GOODWILL'>Goodwill Adjustment</Option>
              <Option value='OTHER'>Other</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='approvedBy'
            label='Approved By'
            rules={[{ required: true, message: 'Please enter approver name' }]}
          >
            <Input placeholder='Name of person approving' />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name='reason'
        label='Detailed Reason'
        rules={[{ required: true, message: 'Please enter detailed reason' }]}
      >
        <TextArea rows={3} placeholder='Provide detailed explanation for the credit note' />
      </Form.Item>

      <Form.Item name='internalNotes' label='Internal Notes (Optional)'>
        <TextArea rows={2} placeholder='Internal notes (not shown on credit note)' />
      </Form.Item>
    </Form>
  )
}

export default CreditNoteForm
